import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { getAppVersion, initializeGlobalVersion } from './version';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    release: `presences@${getAppVersion()}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  });
}

// Initialiser la version globale
initializeGlobalVersion();

/* eslint-disable import/first */
import {
  StyledEngineProvider,
} from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast, Toaster } from 'sonner';
import { getErrorMessage } from './helpers/baserowFetch';

import ArchivePage from './components/ArchivePage';
import PresencePage from './components/PresencePage';

import ExpiredPrefPage, { expiredPref } from './components/ExpiredPrefPage';
import TTCount from './components/TTCount';
import DarkThemeProvider from './DarkThemeProvider';
import useSentryUser from './hooks/useSentryUser';
/* eslint-enable import/first */

const SentryUser = () => {
  useSentryUser();
  return null;
};

const handleGlobalError = (error) => {
  toast.error(getErrorMessage(error));
  Sentry.captureException(error);
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleGlobalError }),
  mutationCache: new MutationCache({ onError: handleGlobalError }),
});

const arePrefsExpired = expiredPref.length;

// Modern React 18+ root API (required for React 19)
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <SentryUser />
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <DarkThemeProvider>
          <CssBaseline />
          <Toaster richColors visibleToasts={7} position="top-right" />
          {arePrefsExpired
            ? <ExpiredPrefPage />
            : (
              <Router>
                <Routes>
                  <Route path="/tt" element={<TTCount />} />
                  <Route path="/archives" element={<ArchivePage />} />
                  <Route path="/" element={<PresencePage />} />
                  <Route path="/:place" element={<PresencePage />} />
                  <Route path="/:place/:day" element={<PresencePage />} />
                  <Route path="*" element={<div>Error 404</div>} />
                </Routes>
              </Router>
            )}
        </DarkThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
